import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef, AfterViewInit, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridModule } from 'ag-grid-angular';
import { GridApi, ChartRef, CreateRangeChartParams, ChartModel } from 'ag-grid-community';

@Component({
  selector: 'app-ag-grid-chart',
  standalone: true,
  imports: [CommonModule, AgGridModule],
  templateUrl: './ag-grid-chart.component.html',
  styleUrls: ['./ag-grid-chart.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgGridChartComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() gridApi!: GridApi;
  @Input() chartType: string = 'bar';
  @Input() chartModel: ChartModel | null = null;

  @Output() chartTypeChange = new EventEmitter<string>();
  @Output() chartModelChange = new EventEmitter<ChartModel>();

  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

  private chartRef: ChartRef | undefined;
  private viewInitialized = false;
  private currentChartType: string = 'bar';
  private currentChartId: string | null = null;
  private isChartUpdatePending = false;
  private chartUpdateTimeoutId: any = null;
  private chartMenuObserver: MutationObserver | null = null;
  private debouncedEmitTimeoutId: any = null;
  private lastKnownChartModel: ChartModel | null = null;
  private seriesSelectionSyncTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastAppliedSeriesLabel = '';
  /** Debounce MutationObserver callback - was firing on every DOM mutation (incl. Copilot) causing CPU spike */
  private mutationObserverDebounceId: ReturnType<typeof setTimeout> | null = null;
  private pendingMutations: MutationRecord[] = [];
  private static readonly SERIES_SYNC_DELAY_MS = 70;
  private static readonly DEBUG_SERIES_SYNC = true;

  private debugLog(...args: any[]): void {
    if (!AgGridChartComponent.DEBUG_SERIES_SYNC) return;
    const normalized = args.map((item) => {
      if (item === undefined) return 'undefined';
      if (item === null) return 'null';
      if (typeof item === 'string') return item;
      try {
        return JSON.stringify(item);
      } catch {
        return String(item);
      }
    });
    // Copy/paste friendly, single-line plain text log
    // eslint-disable-next-line no-console
    console.log(`[ChartSeriesSync] ${normalized.join(' | ')}`);
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.scheduleChartUpdate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewInitialized) {
      return;
    }

    if (changes['gridApi']) {
      this.destroyChart();
      this.scheduleChartUpdate();
      return;
    }

    if (changes['chartModel']) {
      const prev = changes['chartModel'].previousValue;
      const curr = changes['chartModel'].currentValue;

      // Only react when first receiving a model (restore case); avoid reacting to
      // chart emissions flowing back down, which would cause destroy/recreate loop and blinking
      if (!prev && curr) {
        this.destroyChart();
        this.scheduleChartUpdate();
      }
    }
  }

  ngOnDestroy(): void {
    this.removeChartEventListeners();
    this.stopChartMenuObserver();
    if (this.debouncedEmitTimeoutId) {
      clearTimeout(this.debouncedEmitTimeoutId);
    }
    if (this.mutationObserverDebounceId !== null) {
      clearTimeout(this.mutationObserverDebounceId);
      this.mutationObserverDebounceId = null;
    }
    if (this.seriesSelectionSyncTimeoutId !== null) {
      clearTimeout(this.seriesSelectionSyncTimeoutId);
      this.seriesSelectionSyncTimeoutId = null;
    }
    this.destroyChart();
  }

  private removeChartEventListeners(): void {
    if (this.gridApi) {
      if ((this as any)._chartOptionsListener) {
        this.gridApi.removeEventListener('chartOptionsChanged', (this as any)._chartOptionsListener);
        delete (this as any)._chartOptionsListener;
      }
      if ((this as any)._chartRangeListener) {
        this.gridApi.removeEventListener('chartRangeSelectionChanged', (this as any)._chartRangeListener);
        delete (this as any)._chartRangeListener;
      }
      if ((this as any)._chartCreatedListener) {
        this.gridApi.removeEventListener('chartCreated', (this as any)._chartCreatedListener);
        delete (this as any)._chartCreatedListener;
      }
    }
  }

  private destroyChart(): void {
    if (this.chartRef) {
      try {
        if (typeof (this.chartRef as any).destroyChart === 'function') {
          (this.chartRef as any).destroyChart();
        }
      } catch {
      } finally {
        this.chartRef = undefined;
        this.currentChartId = null;
      }
    }

    if (this.chartContainer?.nativeElement) {
      while (this.chartContainer.nativeElement.firstChild) {
        this.chartContainer.nativeElement.removeChild(this.chartContainer.nativeElement.firstChild);
      }
    }
  }

  private scheduleChartUpdate(): void {
    if (this.chartUpdateTimeoutId !== null) {
      clearTimeout(this.chartUpdateTimeoutId);
    }
    this.chartUpdateTimeoutId = setTimeout(() => {
      this.chartUpdateTimeoutId = null;
      this.updateChart();
    }, 100);
  }

  private updateChart(): void {
    if (!this.gridApi || !this.chartContainer) {
      return;
    }

    if (this.isChartUpdatePending) {
      return;
    }

    this.isChartUpdatePending = true;

    try {
      if (this.chartModel) {
        this.chartRef = this.gridApi.restoreChart(
          this.chartModel,
          this.chartContainer.nativeElement
        );
        this.currentChartId = (this.chartRef as any)?.chartId || null;
        this.lastKnownChartModel = this.chartModel;
        if (this.chartModel.chartType) {
          this.currentChartType = this.chartModel.chartType;
          this.chartTypeChange.emit(this.currentChartType);
        }
        this.setupChartEventListeners();
        setTimeout(() => this.updateLastKnownChartModel(), 100);
      } else {
        this.currentChartType = this.chartType;
        this.createNewChart();
      }
    } catch {
      this.createNewChart();
    } finally {
      this.isChartUpdatePending = false;
    }
  }

  private updateLastKnownChartModel(): void {
    const chartModel = this.fetchChartModelFromGrid();
    if (chartModel) {
      this.lastKnownChartModel = chartModel;
      this.currentChartType = chartModel.chartType;
      this.chartTypeChange.emit(chartModel.chartType);
      this.chartModelChange.emit(chartModel);
    }
  }

  private createNewChart(): void {
    if (!this.gridApi || !this.chartContainer) {
      return;
    }

    try {
      const allColumns = this.gridApi.getColumns();
      if (!allColumns || allColumns.length === 0) {
        return;
      }

      const columnIds = allColumns.map(col => col.getColId());

      const params: CreateRangeChartParams = {
        chartType: this.getAgGridChartType(this.chartType),
        cellRange: {
          columns: columnIds
        },
        chartContainer: this.chartContainer.nativeElement,
        suppressChartRanges: true,
        chartThemeOverrides: {
          common: {
            title: {
              enabled: false
            },
            padding: {
              top: 10,
              right: 10,
              bottom: 10,
              left: 10
            }
          }
        }
      };

      this.chartRef = this.gridApi.createRangeChart(params);

      if (this.chartRef) {
        this.currentChartId = (this.chartRef as any)?.chartId || null;
        this.setupChartEventListeners();
        setTimeout(() => this.updateLastKnownChartModel(), 100);
      }
    } catch {
    }
  }

  private getAgGridChartType(type: string): any {
    const typeMap: { [key: string]: string } = {
      'column': 'column',
      'groupedColumn': 'groupedColumn',
      'stackedColumn': 'stackedColumn',
      'normalizedColumn': 'normalizedColumn',
      'bar': 'bar',
      'groupedBar': 'groupedBar',
      'stackedBar': 'stackedBar',
      'normalizedBar': 'normalizedBar',
      'line': 'line',
      'area': 'area',
      'stackedArea': 'stackedArea',
      'normalizedArea': 'normalizedArea',
      'pie': 'pie',
      'doughnut': 'doughnut',
      'scatter': 'scatter',
      'bubble': 'bubble',
      'histogram': 'histogram'
    };

    return typeMap[type] || 'bar';
  }

  public getChartModel(): ChartModel | null {
    const chartModel = this.fetchChartModelFromGrid();
    if (chartModel) {
      this.lastKnownChartModel = chartModel;
      return chartModel;
    }
    return this.lastKnownChartModel;
  }

  public getChartId(): string | null {
    const chartModel = this.getChartModel();
    return chartModel?.chartId || null;
  }

  public getChartContainer(): HTMLElement | null {
    return this.chartContainer?.nativeElement || null;
  }

  private setupChartEventListeners(): void {
    if (!this.gridApi) return;

    this.removeChartEventListeners();

    const optionsListener = (event: any) => {
      this.onChartChanged(event);
    };
    (this as any)._chartOptionsListener = optionsListener;
    this.gridApi.addEventListener('chartOptionsChanged', optionsListener);

    const rangeListener = (event: any) => {
      this.onChartChanged(event);
    };
    (this as any)._chartRangeListener = rangeListener;
    this.gridApi.addEventListener('chartRangeSelectionChanged', rangeListener);

    const chartCreatedListener = (event: any) => {
      this.onChartChanged(event);
    };
    (this as any)._chartCreatedListener = chartCreatedListener;
    this.gridApi.addEventListener('chartCreated', chartCreatedListener);

    this.startChartMenuObserver();
  }

  private onChartChanged(event: any): void {
    this.debugLog('onChartChanged', {
      type: event?.type,
      chartId: event?.chartId,
      chartType: event?.chartType,
      hasChartOptions: !!event?.chartOptions
    });
    if (event?.type === 'chartOptionsChanged' || event?.type === 'chartRangeSelectionChanged') {
      this.scheduleSyncSeriesSelectionFromPanel();
    }
    this.debouncedEmitChartModel();
  }

  private debouncedEmitChartModel(): void {
    if (this.debouncedEmitTimeoutId) {
      clearTimeout(this.debouncedEmitTimeoutId);
    }

    this.debouncedEmitTimeoutId = setTimeout(() => {
      this.debouncedEmitTimeoutId = null;
      this.emitCurrentChartModelWithRetry();
    }, 100);
  }

  private emitCurrentChartModelWithRetry(retryCount: number = 0): void {
    const maxRetries = 5;
    const chartModel = this.fetchChartModelFromGrid();
    
    if (chartModel) {
      this.debugLog('emitCurrentChartModelWithRetry: model found', {
        retryCount,
        chartId: chartModel.chartId,
        chartType: chartModel.chartType,
        cellRangeColumns: (chartModel.cellRange as any)?.columns
      });
      this.lastKnownChartModel = chartModel;
      this.currentChartType = chartModel.chartType;
      this.chartTypeChange.emit(chartModel.chartType);
      this.chartModelChange.emit(chartModel);
      this.normalizeAccumulatedPieSeries(chartModel);
    } else if (retryCount < maxRetries) {
      this.debugLog('emitCurrentChartModelWithRetry: model missing, retrying', { retryCount });
      setTimeout(() => this.emitCurrentChartModelWithRetry(retryCount + 1), 50);
    }
  }

  /**
   * AG Grid can accumulate multiple selected metrics for pie in cellRange:
   * ["Region", "NEW metric", "OLD metric", ...]
   * For this canvas flow we need exactly one metric. Keep category + latest metric.
   */
  private normalizeAccumulatedPieSeries(model: ChartModel): void {
    if (!this.gridApi) return;
    const type = (model.chartType || '').toLowerCase();
    if (type !== 'pie' && type !== 'doughnut') return;
    const cols = this.getCellRangeColumnIds(model);
    if (cols.length <= 2) return;

    const categoryColId = cols[0];
    const metricCols = cols.slice(1);
    if (!categoryColId || metricCols.length === 0) return;

    // AG Grid behavior observed in logs: newest selected metric is prepended.
    // Keep category + first metric to avoid sticky fallback to previous metric.
    const targetMetricColId = metricCols[0];

    this.debugLog('normalizeAccumulatedPieSeries: collapsing range', {
      chartId: model.chartId,
      beforeColumns: cols,
      appliedColumns: [categoryColId, targetMetricColId]
    });

    try {
      this.gridApi.updateChart({
        type: 'rangeChartUpdate',
        chartId: model.chartId,
        cellRange: { columns: [categoryColId, targetMetricColId] }
      } as any);
    } catch {
      // no-op
    }
  }

  private getCellRangeColumnIds(model: ChartModel): string[] {
    const refs = (((model.cellRange as any)?.columns || []) as any[]);
    if (!Array.isArray(refs)) return [];
    return refs
      .map((ref) => (typeof ref === 'string' ? ref : ref?.getColId?.()))
      .filter((id) => typeof id === 'string' && id.length > 0);
  }

  private fetchChartModelFromGrid(): ChartModel | null {
    if (!this.gridApi) return null;
    const chartModels = this.gridApi.getChartModels();
    if (!chartModels || chartModels.length === 0) return null;

    if (this.currentChartId) {
      const matchingChart = chartModels.find(m => m.chartId === this.currentChartId);
      if (matchingChart) {
        this.currentChartId = matchingChart.chartId;
        return matchingChart;
      }
    }

    const latestChart = chartModels[chartModels.length - 1];
    this.currentChartId = latestChart.chartId;
    return latestChart;
  }

  private getChartModelById(chartId: string): ChartModel | null {
    if (!this.gridApi || !chartId) return null;
    const chartModels = this.gridApi.getChartModels();
    if (!chartModels || chartModels.length === 0) return null;
    const match = chartModels.find((m) => m.chartId === chartId) || null;
    if (match) {
      this.currentChartId = match.chartId;
    }
    return match;
  }

  private scheduleSyncSeriesSelectionFromPanel(): void {
    this.debugLog('scheduleSyncSeriesSelectionFromPanel');
    if (this.seriesSelectionSyncTimeoutId !== null) {
      clearTimeout(this.seriesSelectionSyncTimeoutId);
    }
    this.seriesSelectionSyncTimeoutId = setTimeout(() => {
      this.seriesSelectionSyncTimeoutId = null;
      this.syncSeriesSelectionFromPanel();
    }, AgGridChartComponent.SERIES_SYNC_DELAY_MS);
  }

  private syncSeriesSelectionFromPanel(): void {
    if (!this.gridApi) return;
    const chartId = this.currentChartId || this.getChartModel()?.chartId;
    if (!chartId) {
      this.debugLog('syncSeriesSelectionFromPanel: no chartId');
      return;
    }

    const host = this.chartContainer?.nativeElement as HTMLElement | undefined;
    if (!host) {
      this.debugLog('syncSeriesSelectionFromPanel: no host');
      return;
    }

    const displays = host.querySelectorAll('.ag-chart-tabbed-menu .ag-picker-field-display');
    if (!displays || displays.length === 0) {
      this.debugLog('syncSeriesSelectionFromPanel: picker displays not found', { displaysCount: displays?.length || 0 });
      return;
    }

    const displayTexts = Array.from(displays)
      .map((el) => (el as HTMLElement).innerText?.replace(/\s+/g, ' ').trim() || '')
      .filter((text) => text.length > 0);

    // In AG Grid setup panel there can be extra picker displays and both NEW/OLD series
    // may coexist briefly, e.g. ["Region", "SUM ...", "AVG ..."].
    // We must prefer the new user-picked metric, which is typically the first non-category
    // value that differs from the last applied series.
    const categoryLabel = displayTexts[0] || '';
    const metricCandidates = displayTexts.filter((t) => t !== categoryLabel);
    const seriesLabel =
      metricCandidates.find((t) => t !== this.lastAppliedSeriesLabel)
      || metricCandidates[0]
      || '';

    this.debugLog('syncSeriesSelectionFromPanel: raw picker displays', {
      displayTexts,
      displayCount: displays.length
    });

    this.debugLog('syncSeriesSelectionFromPanel: labels', {
      chartId,
      categoryLabel,
      seriesLabel,
      lastAppliedSeriesLabel: this.lastAppliedSeriesLabel
    });
    // If picker UI is stale (often in SUM -> AVG first click), infer newest metric from model.
    let effectiveSeriesLabel = seriesLabel;
    if (!effectiveSeriesLabel || effectiveSeriesLabel === this.lastAppliedSeriesLabel) {
      const model = this.getChartModelById(chartId) || this.getChartModel();
      const modelCols = model ? this.getCellRangeColumnIds(model) : [];
      const inferredSeriesColId = modelCols.length > 1 ? modelCols[1] : null; // newest metric tends to be prepended
      const inferredSeriesLabel = inferredSeriesColId ? this.getDisplayLabelForColId(inferredSeriesColId) : '';
      this.debugLog('syncSeriesSelectionFromPanel: stale picker fallback', {
        modelCols,
        inferredSeriesColId,
        inferredSeriesLabel
      });
      if (inferredSeriesLabel && inferredSeriesLabel !== this.lastAppliedSeriesLabel) {
        effectiveSeriesLabel = inferredSeriesLabel;
      }
    }
    if (!effectiveSeriesLabel || effectiveSeriesLabel === this.lastAppliedSeriesLabel) {
      this.debugLog('syncSeriesSelectionFromPanel: skipped (empty or unchanged series label)');
      return;
    }

    const allCols = this.gridApi.getColumns() || [];
    const categoryColId = this.resolveColIdFromLabel(categoryLabel)
      || allCols.find((c) => (c.getColDef?.() as any)?.chartDataType === 'category')?.getColId()
      || null;
    const seriesColId = this.resolveColIdFromLabel(effectiveSeriesLabel);
    this.debugLog('syncSeriesSelectionFromPanel: resolved colIds', {
      categoryColId,
      seriesColId,
      totalColumns: allCols.length
    });
    if (!seriesColId || !categoryColId) {
      this.debugLog('syncSeriesSelectionFromPanel: unable to resolve category/series column id');
      return;
    }

    const beforeModel = this.getChartModelById(chartId) || this.getChartModel();
    this.debugLog('syncSeriesSelectionFromPanel: before update', {
      chartId,
      beforeColumns: (beforeModel?.cellRange as any)?.columns,
      beforeChartType: beforeModel?.chartType
    });

    try {
      // For pie/donut keep strictly one category + one metric; avoid AG Grid accumulating extra series.
      this.gridApi.updateChart({
        type: 'rangeChartUpdate',
        chartId,
        cellRange: { columns: [categoryColId, seriesColId] }
      } as any);
      this.lastAppliedSeriesLabel = effectiveSeriesLabel;
      const afterModel = this.getChartModelById(chartId) || this.getChartModel();
      this.debugLog('syncSeriesSelectionFromPanel: after update', {
        chartId,
        appliedColumns: [categoryColId, seriesColId],
        afterColumns: (afterModel?.cellRange as any)?.columns,
        afterChartType: afterModel?.chartType
      });
      this.debouncedEmitChartModel();
    } catch {
      this.debugLog('syncSeriesSelectionFromPanel: updateChart threw');
    }
  }

  private resolveColIdFromLabel(label?: string): string | null {
    if (!this.gridApi || !label) return null;
    const normalized = label.replace(/\s+/g, ' ').trim().toLowerCase();
    const allCols = this.gridApi.getColumns() || [];
    const match = allCols.find((col) => {
      const def: any = col.getColDef?.() || {};
      const field = (def.field || '').toString().replace(/\s+/g, ' ').trim().toLowerCase();
      const header = (def.headerName || '').toString().replace(/\s+/g, ' ').trim().toLowerCase();
      return (
        field === normalized ||
        header === normalized ||
        field.includes(normalized) ||
        header.includes(normalized) ||
        normalized.includes(field) ||
        normalized.includes(header)
      );
    });
    return match ? match.getColId() : null;
  }

  private getDisplayLabelForColId(colId: string): string {
    if (!this.gridApi || !colId) return '';
    const col = (this.gridApi.getColumns() || []).find((c) => c.getColId() === colId);
    if (!col) return '';
    const def: any = col.getColDef?.() || {};
    return (def.headerName || def.field || colId || '').toString().trim();
  }

  private startChartMenuObserver(): void {
    this.stopChartMenuObserver();

    // Observe only the chart container subtree, not document.body, to avoid firing on
    // every DOM mutation (Copilot, markdown, etc.) which caused 100% CPU when open.
    const observeTarget = this.chartContainer?.nativeElement;
    if (!observeTarget) return;

    this.chartMenuObserver = new MutationObserver((mutations) => {
      this.pendingMutations.push(...mutations);
      if (this.mutationObserverDebounceId !== null) {
        clearTimeout(this.mutationObserverDebounceId);
      }
      this.mutationObserverDebounceId = setTimeout(() => {
        this.mutationObserverDebounceId = null;
        const toProcess = this.pendingMutations;
        this.pendingMutations = [];
        for (const mutation of toProcess) {
          for (let i = 0; i < mutation.removedNodes.length; i++) {
            const node = mutation.removedNodes[i] as HTMLElement;
            const isPickerPopup = node.classList?.contains('ag-picker-field-popup');
            if (node.classList && (
              node.classList.contains('ag-chart-settings-wrapper') ||
              node.classList.contains('ag-chart-tabbed-menu') ||
              node.classList.contains('ag-popup') ||
              node.classList.contains('ag-menu') ||
              node.classList.contains('ag-chart-menu') ||
              node.classList.contains('ag-charts-popup') ||
              node.classList.contains('ag-picker-field-popup') ||
              node.querySelector?.('.ag-chart-settings-wrapper') ||
              node.querySelector?.('.ag-chart-tabbed-menu')
            )) {
              this.debouncedEmitChartModel();
              if (isPickerPopup) {
                this.scheduleSyncSeriesSelectionFromPanel();
              }
              break;
            }
          }
        }
      }, 150);
    });

    this.chartMenuObserver.observe(observeTarget, {
      childList: true,
      subtree: true
    });
  }

  private stopChartMenuObserver(): void {
    if (this.mutationObserverDebounceId !== null) {
      clearTimeout(this.mutationObserverDebounceId);
      this.mutationObserverDebounceId = null;
    }
    this.pendingMutations = [];
    if (this.chartMenuObserver) {
      this.chartMenuObserver.disconnect();
      this.chartMenuObserver = null;
    }
  }

  public syncChartModel(): ChartModel | null {
    const chartModel = this.fetchChartModelFromGrid();
    if (chartModel) {
      this.lastKnownChartModel = chartModel;
      this.currentChartType = chartModel.chartType;
      this.chartTypeChange.emit(chartModel.chartType);
      this.chartModelChange.emit(chartModel);
    }
    return this.lastKnownChartModel;
  }

  public forceGetCurrentChartModel(): ChartModel | null {
    const chartModel = this.fetchChartModelFromGrid();
    if (chartModel) {
      this.lastKnownChartModel = chartModel;
    }
    return this.lastKnownChartModel || this.chartModel;
  }

  public getLastKnownChartModel(): ChartModel | null {
    return this.lastKnownChartModel;
  }
}
